import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../../lib/api";
import { Anchor, ArrowRight, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [companyLocked, setCompanyLocked] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  // Pre-flight: ask the backend whether this token belongs to an invite-
  // flow placeholder so we can show / hide the name fields accordingly.
  const [requiresName, setRequiresName] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const [infoLoading, setInfoLoading] = useState(!!token);

  useEffect(() => {
    let alive = true;
    if (!token) { setInfoLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-info?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        setRequiresName(!!data?.requires_name);
        setEmailHint(data?.email || "");
        const pf = data?.prefill || {};
        if (pf.first_name) setFirstName(pf.first_name);
        if (pf.last_name) setLastName(pf.last_name);
        if (pf.phone) setPhone(pf.phone);
        if (pf.company) {
          setCompany(pf.company);
          setCompanyLocked(true);
        }
      } catch (e2) {
        if (!alive) return;
        // Soft-fail: still let the user try, the POST will surface any
        // hard error (expired / used token).
        setErr(fmtErr(e2));
      } finally {
        if (alive) setInfoLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (requiresName) {
      if (!firstName.trim() || !lastName.trim()) {
        return setErr("Please enter your first and last name");
      }
      if (phone.trim().length < 3) {
        return setErr("Please enter a phone number");
      }
    }
    if (pwd.length < 6) return setErr("Password must be at least 6 characters");
    if (pwd !== pwd2) return setErr("Passwords don't match");
    setBusy(true);
    try {
      const payload = { token, new_password: pwd };
      if (requiresName) {
        payload.first_name = firstName.trim();
        payload.last_name = lastName.trim();
        payload.phone = phone.trim();
        if (company.trim()) payload.company = company.trim();
      }
      await api.post("/auth/reset-password", payload);
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (e2) {
      const raw = fmtErr(e2);
      setErr(
        raw === "Network Error"
          ? "Can't reach Seavanto right now. Check your internet and tap Update password again — if the problem keeps happening, ask the person who invited you to re-send the link."
          : raw,
      );
    } finally {
      setBusy(false);
    }
  };

  const heading = requiresName ? "Welcome to Seavanto" : "Set a new password";
  const sub = requiresName
    ? "Tell us your name and choose a password to finish setting up your account."
    : "Pick something you'll remember — minimum 6 characters.";

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-zinc-50">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-zinc-200 rounded-lg p-8 space-y-6" data-testid="reset-form">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <div>
          <KeyRound className="h-6 w-6 text-ink mb-3" />
          <h1 className="font-display text-2xl font-medium tracking-tight">{heading}</h1>
          <p className="text-sm text-zinc-600 mt-1">{sub}</p>
          {requiresName && emailHint && (
            <p className="text-xs text-zinc-500 mt-2" data-testid="reset-email-hint">
              Setting up <span className="font-medium text-zinc-700">{emailHint}</span>
            </p>
          )}
        </div>

        {!token && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Missing reset token. Please use the link from your email.
          </div>
        )}

        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-2" data-testid="reset-done">
            <CheckCircle2 className="h-5 w-5 mt-0.5" />
            <div>
              {requiresName ? "Account ready. Redirecting to sign in…" : "Password updated. Redirecting to sign in…"}
            </div>
          </div>
        ) : (
          <>
            {requiresName && (
              <div className="space-y-3" data-testid="reset-name-fields">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first">First name</Label>
                    <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required data-testid="reset-first-name" />
                  </div>
                  <div>
                    <Label htmlFor="last">Last name</Label>
                    <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} required data-testid="reset-last-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+1 555 0000" data-testid="reset-phone" />
                </div>
                {(companyLocked || company) && (
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      readOnly={companyLocked}
                      className={companyLocked ? "bg-zinc-50 text-zinc-600 cursor-not-allowed" : ""}
                      data-testid="reset-company"
                    />
                    {companyLocked && (
                      <p className="text-[11px] text-zinc-500 mt-1">
                        Set by the person who invited you. Contact them to change it.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="pwd">{requiresName ? "Choose a password" : "New password"}</Label>
              <Input id="pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required data-testid="reset-pwd" />
            </div>
            <div>
              <Label htmlFor="pwd2">Confirm password</Label>
              <Input id="pwd2" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required data-testid="reset-pwd2" />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
            <Button type="submit" disabled={busy || !token || infoLoading} className="w-full bg-ink hover:bg-black" data-testid="reset-submit">
              {busy
                ? (requiresName ? "Creating account…" : "Updating…")
                : (requiresName ? "Create account" : "Update password")}
              {" "}<ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {requiresName && (
              <p className="text-[11px] text-zinc-500" data-testid="reset-invite-hint">
                Once you sign in, you'll be asked whether to accept the yacht invitation.
              </p>
            )}
          </>
        )}

        <div className="text-sm text-center">
          <Link to="/login" className="text-zinc-600 hover:text-ink">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
